import * as React from 'react';
import { observer } from 'mobx-react';
import dashboardWrapper from '~/wrappers/dashboard';
import { t } from '~/i18n';
import { Panel, TabsPanel, CreateButton } from '@ixicare/ui';
import { HcpGroupsTable } from '~/components/HcpGroupsTable';
import { HcpGroupCreateEditModal } from '~/containers/HcpGroups/HcpGroupCreateEditModal';
import { HcpAssignmentTab } from '~/containers/HcpGroups/HcpAssignmentTab';
import { ZoneAssignmentTab } from '~/containers/HcpGroups/ZoneAssignmentTab';
import { ResidentAssignmentTab } from '~/containers/HcpGroups/ResidentAssignmentTab';
import { useSiteConfiguration } from '~containers/ConfigurationForm/ConfigurationForm';
import { useStores } from '~store/utils';

const HcpGroupsPage = observer(() => {
  const { permissions, sites } = useStores();
  const creationPermission = permissions.has('can_create_hcp_groups');
  const editPermission = permissions.has('can_edit_all_hcp_groups');
  const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);

  const hcpGroupsTable = React.useMemo(() => {
    return <HcpGroupsTable canCreate={creationPermission} canEdit={editPermission} />;
  }, [creationPermission, editPermission]);

  const tabs = React.useMemo(() => {
    return [
      {
        label: t('hcpGroups.tabs.groups'),
        id: 'groups',
        content: hcpGroupsTable,
      },
      {
        label: t('hcpGroups.tabs.assignHcps'),
        id: 'assignHcps',
        content: <HcpAssignmentTab />,
      },
      {
        label: t('hcpGroups.tabs.assignZones'),
        id: 'assignZones',
        content: <ZoneAssignmentTab />,
      },
      {
        label: t('hcpGroups.tabs.assignResidents.title'),
        id: 'assignResidents',
        content: <ResidentAssignmentTab />,
      },
    ];
  }, [hcpGroupsTable]);

  const { data: siteConfig } = useSiteConfiguration(sites.selectedSite?.uuid ?? null);
  const isSegmentationEnabled = siteConfig?.['enable-segmentation'] ?? false;

  return (
    <>
      {!isSegmentationEnabled && (
        <div
          style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
          }}
        >
          <p style={{ margin: 0 }}>
            {t('hcpGroups.segmentation.disabled.message')}{' '}
            <a href="/configuration" style={{ textDecoration: 'underline', color: '#856404', fontWeight: 'bold' }}>
              {t('hcpGroups.segmentation.disabled.link')}
            </a>
          </p>
        </div>
      )}
      <Panel>{editPermission ? <TabsPanel tabs={tabs} useHash={true} /> : hcpGroupsTable}</Panel>
      {creationPermission && <CreateButton atBottom onClick={() => setShowCreateModal(true)} />}
      {showCreateModal && <HcpGroupCreateEditModal onClose={() => setShowCreateModal(false)} />}
    </>
  );
});

export default dashboardWrapper(HcpGroupsPage, t('menu.dashboard.hcpGroups'));
